import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PastorResponse } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { tenancyApi } from '@/services/tenancy.api';
import { filesApi } from '@/services/files.api';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';
import { Alert, Button, EmptyState, Field, Spinner } from '@/components/ui';
import { Avatar } from '@/components/avatar';

const QUERY_KEY = ['tenancy', 'pastors'];

interface PastorFormValues {
  readonly name: string;
  readonly roleTitle: string;
  readonly phone: string;
  readonly email: string;
  readonly spousePastorId: string;
}

const EMPTY_FORM: PastorFormValues = {
  name: '',
  roleTitle: '',
  phone: '',
  email: '',
  spousePastorId: '',
};

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/**
 * "Pastores" — a church's pastoral leadership directory. A church can have
 * several pastor couples; `spousePastorId` links two entries together and
 * the backend keeps that link mutual, so picking a spouse from either side
 * pairs them the same way.
 */
export function PastorsTab({ canAdmin }: { readonly canAdmin: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const showArchived = getShowArchivedRecordsPreference();

  const pastors = useQuery({
    queryKey: [...QUERY_KEY, showArchived],
    queryFn: ({ signal }) => tenancyApi.listPastors(showArchived, signal),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const list = pastors.data ?? [];
  const activePastors = list.filter((pastor) => !pastor.archivedAt);
  const nameOf = (id: string | null) => list.find((pastor) => pastor.id === id)?.name ?? null;

  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      tenancyApi.createPastor({
        name: createForm.name.trim(),
        roleTitle: createForm.roleTitle.trim() || null,
        phone: createForm.phone.trim() || null,
        email: createForm.email.trim() || null,
        spousePastorId: createForm.spousePastorId || null,
      }),
    onSuccess: () => {
      setCreateForm(EMPTY_FORM);
      setCreateError(null);
      invalidate();
    },
    onError: (err: unknown) => setCreateError(errorMessage(err, t)),
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editError, setEditError] = useState<string | null>(null);
  const startEdit = (pastor: PastorResponse) => {
    setEditingId(pastor.id);
    setEditError(null);
    setEditForm({
      name: pastor.name,
      roleTitle: pastor.roleTitle ?? '',
      phone: pastor.phone ?? '',
      email: pastor.email ?? '',
      spousePastorId: pastor.spousePastorId ?? '',
    });
  };

  const update = useMutation({
    mutationFn: (id: string) =>
      tenancyApi.updatePastor(id, {
        name: editForm.name.trim(),
        roleTitle: editForm.roleTitle.trim() || null,
        phone: editForm.phone.trim() || null,
        email: editForm.email.trim() || null,
        spousePastorId: editForm.spousePastorId || null,
      }),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
    onError: (err: unknown) => setEditError(errorMessage(err, t)),
  });

  const archive = useMutation({
    mutationFn: (id: string) => tenancyApi.archivePastor(id),
    onSuccess: invalidate,
  });
  const restore = useMutation({
    mutationFn: (id: string) => tenancyApi.restorePastor(id),
    onSuccess: invalidate,
  });

  const uploadPhoto = useMutation({
    mutationFn: async ({ pastorId, file }: { pastorId: string; file: File }) => {
      const session = await filesApi.requestUpload({
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        declaredSize: file.size,
        ownerType: 'Pastor',
        ownerId: pastorId,
      });
      await filesApi.putBytes(session.uploadUrl, file);
      const uploaded = await filesApi.completeUpload(session.id, {
        fileName: file.name,
        ownerType: 'Pastor',
        ownerId: pastorId,
      });
      return tenancyApi.updatePastor(pastorId, { photoFileId: uploaded.id });
    },
    onSuccess: invalidate,
  });

  return (
    <div className="tab-fieldset">
      {canAdmin && (
        <form
          className="filters"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            if (createForm.name.trim()) create.mutate();
          }}
        >
          {createError && <Alert tone="danger">{createError}</Alert>}
          <Field
            label={t('administration.pastor.name')}
            name="pastor-name"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            required
          />
          <Field
            label={t('administration.pastor.roleTitle')}
            name="pastor-role"
            value={createForm.roleTitle}
            onChange={(e) => setCreateForm({ ...createForm, roleTitle: e.target.value })}
            placeholder={t('administration.pastor.roleTitlePlaceholder')}
          />
          <Field
            label={t('administration.pastor.phone')}
            name="pastor-phone"
            type="tel"
            value={createForm.phone}
            onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
          />
          <Field
            label={t('administration.pastor.email')}
            name="pastor-email"
            type="email"
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
          />
          <div className="field">
            <label className="field-label" htmlFor="pastor-spouse">
              {t('administration.pastor.spouse')}
            </label>
            <select
              id="pastor-spouse"
              className="field-input"
              value={createForm.spousePastorId}
              onChange={(e) => setCreateForm({ ...createForm, spousePastorId: e.target.value })}
            >
              <option value="">{t('administration.pastor.noSpouse')}</option>
              {activePastors.map((pastor) => (
                <option key={pastor.id} value={pastor.id}>
                  {pastor.name}
                </option>
              ))}
            </select>
          </div>
          <Button type="submit" variant="primary" loading={create.isPending}>
            {t('administration.pastor.create')}
          </Button>
        </form>
      )}

      {pastors.isPending && <Spinner label={t('common.states.loading')} />}
      {pastors.isSuccess && list.length === 0 && (
        <EmptyState title={t('administration.pastor.empty')} />
      )}

      {list.length > 0 && (
        <div className="pastor-grid">
          {list.map((pastor) => {
            const isEditing = editingId === pastor.id;
            const spouseName = nameOf(pastor.spousePastorId);

            if (isEditing) {
              return (
                <form
                  key={pastor.id}
                  className="pastor-card"
                  onSubmit={(event: FormEvent) => {
                    event.preventDefault();
                    if (editForm.name.trim()) update.mutate(pastor.id);
                  }}
                >
                  {editError && <Alert tone="danger">{editError}</Alert>}
                  <Field
                    label={t('administration.pastor.name')}
                    name="edit-pastor-name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                  />
                  <Field
                    label={t('administration.pastor.roleTitle')}
                    name="edit-pastor-role"
                    value={editForm.roleTitle}
                    onChange={(e) => setEditForm({ ...editForm, roleTitle: e.target.value })}
                  />
                  <Field
                    label={t('administration.pastor.phone')}
                    name="edit-pastor-phone"
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                  <Field
                    label={t('administration.pastor.email')}
                    name="edit-pastor-email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                  <div className="field">
                    <label className="field-label" htmlFor={`edit-pastor-spouse-${pastor.id}`}>
                      {t('administration.pastor.spouse')}
                    </label>
                    <select
                      id={`edit-pastor-spouse-${pastor.id}`}
                      className="field-input"
                      value={editForm.spousePastorId}
                      onChange={(e) => setEditForm({ ...editForm, spousePastorId: e.target.value })}
                    >
                      <option value="">{t('administration.pastor.noSpouse')}</option>
                      {activePastors
                        .filter((candidate) => candidate.id !== pastor.id)
                        .map((candidate) => (
                          <option key={candidate.id} value={candidate.id}>
                            {candidate.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="pastor-card-actions">
                    <Button type="submit" variant="primary" loading={update.isPending}>
                      {t('common.actions.save')}
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setEditingId(null)}>
                      {t('common.actions.cancel')}
                    </Button>
                  </div>
                </form>
              );
            }

            return (
              <div key={pastor.id} className="pastor-card" data-archived={!!pastor.archivedAt}>
                <div className="pastor-card-header">
                  {canAdmin ? (
                    <label
                      className="avatar-upload"
                      aria-label={t('administration.pastor.changePhoto')}
                    >
                      <Avatar
                        photoFileId={pastor.photoFileId}
                        displayName={pastor.name}
                        size={56}
                      />
                      <span className="avatar-upload-hint">
                        {t('administration.pastor.changePhoto')}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          event.target.value = '';
                          if (file) uploadPhoto.mutate({ pastorId: pastor.id, file });
                        }}
                      />
                    </label>
                  ) : (
                    <Avatar photoFileId={pastor.photoFileId} displayName={pastor.name} size={56} />
                  )}
                  <div>
                    <div className="pastor-card-name">{pastor.name}</div>
                    {pastor.roleTitle && <div className="pastor-card-role">{pastor.roleTitle}</div>}
                  </div>
                </div>

                <div className="pastor-card-meta">
                  {pastor.phone && <span>{pastor.phone}</span>}
                  {pastor.email && <span>{pastor.email}</span>}
                  {spouseName && (
                    <span>
                      {t('administration.pastor.spouseOf', { values: { name: spouseName } })}
                    </span>
                  )}
                  {pastor.archivedAt && <span>{t('administration.pastor.archived')}</span>}
                </div>

                {canAdmin && (
                  <div className="pastor-card-actions">
                    {pastor.archivedAt ? (
                      <Button
                        variant="ghost"
                        loading={restore.isPending}
                        onClick={() => restore.mutate(pastor.id)}
                      >
                        {t('common.actions.restore')}
                      </Button>
                    ) : (
                      <>
                        <Button variant="secondary" onClick={() => startEdit(pastor)}>
                          {t('common.actions.edit')}
                        </Button>
                        <Button
                          variant="ghost"
                          loading={archive.isPending}
                          onClick={() => archive.mutate(pastor.id)}
                        >
                          {t('common.actions.archive')}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
